/**
 * Pagination utility functions for managing table data pagination
 */

export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalItems: number;
}

export interface PaginationInfo {
  totalPages: number;
  startIndex: number;
  endIndex: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  visiblePages: number[];
}

/**
 * Calculate pagination information based on current state
 */
export const calculatePaginationInfo = (state: PaginationState): PaginationInfo => {
  const { currentPage, pageSize, totalItems } = state;
  
  // Handle "All" case (when pageSize is -1 or greater than totalItems)
  const effectivePageSize = pageSize === -1 || pageSize >= totalItems ? totalItems : pageSize;
  const totalPages = effectivePageSize === totalItems ? 1 : Math.ceil(totalItems / effectivePageSize);
  
  const startIndex = effectivePageSize === totalItems ? 0 : (currentPage - 1) * effectivePageSize;
  const endIndex = effectivePageSize === totalItems ? totalItems : Math.min(startIndex + effectivePageSize, totalItems);
  
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;
  
  // Calculate visible page numbers (show max 7 pages around current)
  const visiblePages = calculateVisiblePages(currentPage, totalPages);
  
  return {
    totalPages,
    startIndex,
    endIndex,
    hasNextPage,
    hasPreviousPage,
    visiblePages
  };
};

/**
 * Calculate which page numbers to show in pagination
 */
export const calculateVisiblePages = (currentPage: number, totalPages: number, maxVisible: number = 7): number[] => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  
  const half = Math.floor(maxVisible / 2);
  let start = currentPage - half;
  let end = currentPage + half;
  
  if (start < 1) {
    start = 1;
    end = maxVisible;
  }
  
  if (end > totalPages) {
    end = totalPages;
    start = totalPages - maxVisible + 1;
  }
  
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

/**
 * Slice data array based on pagination state
 */
export const paginateData = <T>(data: T[], state: PaginationState): T[] => {
  const { startIndex, endIndex } = calculatePaginationInfo(state);
  return data.slice(startIndex, endIndex);
};

/**
 * Get pagination summary text (e.g., "Showing 1-20 of 100 results")
 */
export const getPaginationSummary = (state: PaginationState): string => {
  const { startIndex, endIndex } = calculatePaginationInfo(state);
  const { totalItems } = state;
  
  if (totalItems === 0) {
    return "No results found";
  }
  
  if (startIndex === 0 && endIndex === totalItems) {
    return `Showing all ${totalItems.toLocaleString()} results`;
  }
  
  return `Showing ${(startIndex + 1).toLocaleString()}-${endIndex.toLocaleString()} of ${totalItems.toLocaleString()} results`;
};

/**
 * Page size options
 */
export const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10 per page' },
  { value: 20, label: '20 per page' },
  { value: 50, label: '50 per page' },
  { value: 100, label: '100 per page' },
  { value: -1, label: 'Show all' }
] as const;

/**
 * Default pagination state
 */
export const DEFAULT_PAGINATION_STATE: PaginationState = {
  currentPage: 1,
  pageSize: 20,
  totalItems: 0
};

/**
 * Create a new pagination state with optional overrides
 */
export const createPaginationState = (options: Partial<PaginationState> = {}): PaginationState => {
  return {
    ...DEFAULT_PAGINATION_STATE,
    ...options
  };
};

/**
 * Validate and normalize page number
 */
export const validatePageNumber = (page: number, totalPages: number): number => {
  if (page < 1) return 1;
  if (page > totalPages) return totalPages;
  return page;
};

/**
 * Create pagination change handlers
 */
export const createPaginationHandlers = (
  state: PaginationState,
  onChange: (newState: PaginationState) => void
) => {
  const info = calculatePaginationInfo(state);
  
  return {
    goToPage: (page: number) => {
      const validPage = validatePageNumber(page, info.totalPages);
      onChange({ ...state, currentPage: validPage });
    },
    
    goToNextPage: () => {
      if (info.hasNextPage) {
        onChange({ ...state, currentPage: state.currentPage + 1 });
      }
    },
    
    goToPreviousPage: () => {
      if (info.hasPreviousPage) {
        onChange({ ...state, currentPage: state.currentPage - 1 });
      }
    },
    
    goToFirstPage: () => {
      onChange({ ...state, currentPage: 1 });
    },
    
    goToLastPage: () => {
      onChange({ ...state, currentPage: info.totalPages });
    },
    
    changePageSize: (newPageSize: number) => {
      // When changing page size, try to keep the user on a similar position
      const currentStartIndex = (state.currentPage - 1) * state.pageSize;
      const newPage = newPageSize === -1 ? 1 : Math.floor(currentStartIndex / newPageSize) + 1;
      
      onChange({
        ...state,
        pageSize: newPageSize,
        currentPage: newPage
      });
    }
  };
};